import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { Flame, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { toneSoftClasses, toneSolidClasses, toneTextClasses } from "@/lib/tones";
import {
  transitionDetailLine,
  transitionLabel,
  transitionWindow,
} from "@/lib/linkedin-transitions";
import { linkedInApi } from "@/services/linkedin-api";

const SINAIS_POR_PAGINA = 50;
const PERIODOS_EM_DIAS = [7, 30, 90] as const;
type PeriodoEmDias = (typeof PERIODOS_EM_DIAS)[number];

/** Mudanças vistas no LinkedIn entre duas buscas do mesmo perfil, mais recentes primeiro. */
export function LinkedInSinais() {
  const [apenasQuentes, setApenasQuentes] = useState(true);
  const [dias, setDias] = useState<PeriodoEmDias>(30);
  const [limite, setLimite] = useState(SINAIS_POR_PAGINA);

  const sinaisQuery = useQuery({
    queryKey: ["linkedin-sinais", dias, apenasQuentes, limite],
    queryFn: () => linkedInApi.listSinais({ dias, apenasQuentes, limit: limite }),
    placeholderData: (previous) => previous,
  });
  const sinais = sinaisQuery.data?.items ?? [];
  const total = sinaisQuery.data?.total ?? 0;

  const trocarPeriodo = (periodo: PeriodoEmDias) => {
    setDias(periodo);
    setLimite(SINAIS_POR_PAGINA);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {PERIODOS_EM_DIAS.map((periodo) => (
            <Button
              key={periodo}
              size="sm"
              variant={dias === periodo ? "secondary" : "ghost"}
              onClick={() => trocarPeriodo(periodo)}
            >
              {periodo} dias
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="linkedin-sinais-quentes"
            checked={apenasQuentes}
            onCheckedChange={(valor) => {
              setApenasQuentes(valor);
              setLimite(SINAIS_POR_PAGINA);
            }}
          />
          <Label htmlFor="linkedin-sinais-quentes" className="text-sm">
            Só sinais quentes
          </Label>
        </div>
      </div>

      {sinaisQuery.isPending ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : sinaisQuery.isError ? (
        <div className="py-10 text-center text-sm text-muted-foreground">
          Não foi possível carregar os sinais. Tente de novo em instantes.
        </div>
      ) : sinais.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground">
          {apenasQuentes
            ? `Nenhum sinal quente nos últimos ${dias} dias.`
            : `Nenhuma mudança vista no LinkedIn nos últimos ${dias} dias.`}
        </div>
      ) : (
        <>
          <div className="divide-y rounded-lg border bg-card">
            {sinais.map((sinal) => {
              const detalhe = transitionDetailLine(sinal);
              return (
                <div key={sinal.id} className="flex items-start gap-3 px-3 py-2.5">
                  <span
                    className={cn(
                      "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                      sinal.quente ? toneSolidClasses.warning : "bg-muted-foreground/30",
                    )}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                      <Link
                        to={`/freelancers/${sinal.freelancer_id}`}
                        className="font-medium text-primary underline-offset-2 hover:underline"
                      >
                        {sinal.freelancer_nome}
                      </Link>
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 rounded-full border px-1.5 text-xs",
                          sinal.quente ? toneSoftClasses.warning : toneSoftClasses.neutral,
                        )}
                      >
                        {sinal.quente && <Flame className="h-3 w-3" />}
                        {transitionLabel(sinal)}
                      </span>
                    </div>
                    {detalhe && <div className="truncate text-sm">{detalhe}</div>}
                    <div
                      className={cn(
                        "text-xs",
                        sinal.quente ? toneTextClasses.warning : "text-muted-foreground",
                      )}
                    >
                      {transitionWindow(sinal.observado_em, sinal.observado_antes_em)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {total > sinais.length && (
            <div className="flex justify-center pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLimite((atual) => atual + SINAIS_POR_PAGINA)}
                disabled={sinaisQuery.isFetching}
              >
                Mostrar mais sinais ({total - sinais.length} restantes)
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
